import numberFormat from "@/helpers/numberFormat";
import { truncateString } from "@/helpers/string";
import { Link } from "@inertiajs/inertia-react";
import { DateTime } from "luxon";
import { useMemo } from "react";
import classNames from "classnames";
import Validations from "./Validations";
import Validation from "./Validation";
import { showJSONData } from "@/helpers/example";

const OrderCard = ({ office, title, products, type: orderType, types, store, ...props }) => {
    const getOrdersFilteredByType = (type = '') => {
        if (!types.includes(type)) {
            throw new Error(`Type "${type}" is unexpected, was expected: ${types.join(', ')}`);
        }
        return props.orders.filter((order) => order.parsed.type === type);
    }

    const orders = useMemo(() => {
        return getOrdersFilteredByType(orderType);
    }, [orderType])

    const schedules = orders.map(order => (order.schedules.map(schedule => {
        const getOrder = () => {
            const order = orders.find(order => order.id === schedule.pivot.order_id);
            return order;
        }
        return {
            ...schedule,
            getOrder,
        }
    }))).flat();

    const [totalTickets, totalPrice] = orders.reduce((acc, order) => {
        const tickets_number = order.parsed.tickets_number;
        return [acc[0] + tickets_number, acc[1] + (order.parsed.totals.total)];
    }, [0, 0]);

    const [totalTicketsIva, totalPriceIva] = orders.reduce((acc, order) => {
        const tickets_number = order.parsed.tickets_number;
        return [acc[0] + tickets_number, acc[1] + (order.parsed.totals_iva.total)];
    }, [0, 0]);

    const formatPrice = (price) => numberFormat(price, 0, '', '.');

    return (
        <>
            {orders.length > 0 && (
                <div className='relative px-6 py-4 bg-white shadow-md overflow-x-auto w-full sm:rounded-lg'>
                    <div className="flex align-items-end gap-4 mb-2">
                        <h2 className='text-gray-900 text-lg font-bold'>{title}</h2>
                    </div>
                    <table className='w-full text-left border-separate overflow-x-scroll'>
                        <thead>
                            <tr>
                                {[
                                    ['Orden', 'id'],
                                    ['Folio', 'id'],
                                    ['Val. Caja', 'validation'],
                                    [store === 'cerogrado' ? 'Val. Pista' : 'Val. Tramp', 'validation'],
                                    ['Precio ticket', 'price'],
                                    ['Cant. tickets', 'quantity'],
                                    ...products.map(({ name }) => ([truncateString(name, 14), 'product'])),
                                    ['Descuento', 'price'],
                                    ['Total s/IVA', 'price'],
                                    ['Total c/IVA', 'price']
                                ]
                                .filter(([title, type]) => {
                                    if (orderType === 'bulk') {
                                        if (['price', 'quantity', 'product'].includes(type)) {
                                            return false;
                                        }
                                    }

                                    if ((orderType === 'product-manual' || orderType === 'product-redelcom')) {
                                        if (['validation', 'quantity'].includes(type) || ['Precio ticket', 'Descuento'].includes(title)) {
                                            return false
                                        }
                                    }

                                    return true;
                                })
                                .map(([header, type], index) => (
                                    <th
                                        colSpan={(orderType === 'product-manual' || orderType === 'product-redelcom') ? 2 : 1}
                                        className={classNames({
                                            "pr-6": index === 0,
                                            "px-6": index !== 0,
                                        })}
                                        key={`order-card-header-${type}-${title}-${index}`}
                                    >
                                        {header}
                                    </th>
                                ))}

                                {orderType === 'bulk' && (
                                    <th
                                        className="px-6"
                                    >
                                        Nombre empresa
                                    </th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {orders.map(order => {
                                const orderSchedules = schedules.filter(schedule => schedule.getOrder().id === order.id);
                                const tickets_number = order.parsed.tickets_number;
                                const ticket_price = order.parsed.price;
                                return (
                                    <tr key={order.id} className="text-gray-600">
                                        {[
                                            [
                                                <Link href={route('order.show', { uuid: order.uuid, office: office.slug })} className="hover:underline">
                                                    {order.id}
                                                </Link>,
                                                'id'
                                            ],
                                            [
                                                order.softland_order ?? '-',
                                                'id'
                                            ],
                                            ...['caja', 'pista'].map((validationName) => ([
                                                <Validation store={store} office={office} orderSchedules={orderSchedules} order={order} validationName={validationName} />,
                                                'validation'
                                            ])),
                                            [
                                                `$${formatPrice(ticket_price)}`,
                                                'ticket_price'
                                            ],
                                            [
                                                tickets_number,
                                                'quantity'
                                            ],
                                            ...products.map(product => {
                                                const productOrder = order.products.find(productOrder => productOrder.id === product.id);
                                                return [productOrder ? `$${formatPrice((productOrder.pivot.price / 1.19) * productOrder.pivot.quantity)} ${productOrder.pivot.quantity > 1 ? `($${formatPrice(productOrder.pivot.price / 1.19)} x ${productOrder.pivot.quantity})` : ''}` : '-', 'product']
                                            }),
                                            [
                                                `$${formatPrice(order.parsed.totals.total_discount)}`,
                                                'discount_price'
                                            ],
                                            [
                                                `$${formatPrice(order.parsed.totals.total)}`,
                                                'price'
                                            ],
                                            [
                                                `$${formatPrice(order.parsed.totals_iva.total)}`,
                                                'price'
                                            ]
                                        ]
                                        .filter(([title, type]) => {
                                            if (orderType === 'bulk') {
                                                if (['price', 'quantity', 'product', 'ticket_price', 'discount_price'].includes(type)) {
                                                    return false;
                                                }
                                            }

                                            if ((orderType === 'product-manual' || orderType === 'product-redelcom')) {
                                                if (['validation', 'quantity', 'ticket_price', 'discount_price'].includes(type)) {
                                                    return false
                                                }
                                            }

                                            return true;
                                        })
                                        .map(([component, type], index) => (
                                            <td
                                                colSpan={(orderType === 'product-manual' || orderType === 'product-redelcom') ? 2 : 1}
                                                className={classNames({
                                                    "pr-6": index === 0,
                                                    "px-6": index !== 0,
                                                })}
                                                key={`order-card-data-${type}-${index}`}
                                            >
                                                {component}
                                            </td>
                                        ))}

                                        {orderType === 'bulk' && (
                                            <td
                                                className="px-6"
                                            >
                                                {order.bulk_order.bulk_sale.company_name}
                                            </td>
                                        )}


                                        {/* <td className="pr-6">
                                            <Link href={route('order.show', { uuid: order.uuid, office: office.slug })} className="hover:underline">
                                                {order.id}
                                            </Link>
                                        </td>

                                        <Validations office={office} orderSchedules={orderSchedules} order={order} />
                                        <td className="px-6">${formatPrice(ticket_price)}</td>
                                        <td className="px-6">{tickets_number}</td>
                                        {products.map(product => (
                                            <td className="px-6" key={`product-list-body-${product.id}`}>
                                                {(() => {
                                                    const productOrder = order.products.find(productOrder => productOrder.id === product.id);
                                                    return productOrder ? `$${formatPrice((productOrder.pivot.price / 1.19) * productOrder.pivot.quantity)} ${productOrder.pivot.quantity > 1 ? `($${formatPrice(productOrder.pivot.price / 1.19)} x ${productOrder.pivot.quantity})` : ''}` : '-'
                                                })()}
                                            </td>
                                        ))}
                                        <td className="px-6">${formatPrice(order.parsed.totals.total_discount)}</td>
                                        <td className="px-6">${formatPrice(order.parsed.totals.total)}</td>
                                        <td className="px-6">${formatPrice(order.parsed.totals_iva.total)}</td> */}
                                    </tr>
                                )
                            })}
                        </tbody>
                        <tfoot>
                            <tr><td colSpan='11' className='pr-6 border-t-2'></td></tr>
                            {(orderType !== 'bulk') && (
                                <tr>
                                    <td colSpan={(orderType === 'product-manual' || orderType === 'product-redelcom') ? 2 : 1} className='font-bold'>Totales</td>
                                    <td colSpan={(orderType === 'product-manual' || orderType === 'product-redelcom') ? 2 : 1} className="px-6"></td>

                                    {(orderType !== 'product-manual' && orderType !== 'product-redelcom') && (
                                        <>
                                            <td colSpan={(orderType === 'product-manual' || orderType === 'product-redelcom') ? 2 : 1} className="px-6"></td>
                                            <td colSpan={(orderType === 'product-manual' || orderType === 'product-redelcom') ? 2 : 1} className="px-6"></td>
                                            <td colSpan={(orderType === 'product-manual' || orderType === 'product-redelcom') ? 2 : 1} className="px-6"></td>
                                        </>
                                    )}

                                    {(orderType !== 'product-manual' && orderType !== 'product-redelcom') && (
                                        <td colSpan={1} className="px-6">{totalTickets}</td>
                                    )}
                                    {products.map((product) => (
                                        <td colSpan={(orderType === 'product-manual' || orderType === 'product-redelcom') ? 2 : 1} key={`product-list-foot-${product.id}`}></td>
                                    ))}
                                    {(orderType !== 'product-manual' && orderType !== 'product-redelcom') && (
                                        <td colSpan={(orderType === 'product-manual' || orderType === 'product-redelcom') ? 2 : 1} className="px-6"></td>
                                    )}
                                    <td colSpan={(orderType === 'product-manual' || orderType === 'product-redelcom') ? 2 : 1} className="px-6">${formatPrice(totalPrice)}</td>
                                    <td colSpan={(orderType === 'product-manual' || orderType === 'product-redelcom') ? 2 : 1} className="px-6">${formatPrice(totalPriceIva)}</td>
                                </tr>
                            )}
                        </tfoot>
                    </table>
                </div>
            )}
        </>
    )
}

export default OrderCard;